import { Dialog } from '@radix-ui/react-dialog';
import { CaretSortIcon, ChatBubbleIcon, CheckIcon, DotsHorizontalIcon, UpdateIcon } from '@radix-ui/react-icons';
import { Fragment, useCallback, useContext, useEffect, useState } from 'react';
import { Controller, useForm, } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router';
import BunnyInfoItem from '../components/bunnyInfoItem';
import AddRegistrationCommentDialog from '../components/dialogs/addRegistrationCommentDialog';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../components/ui/command";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Label } from '../components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { Separator } from '../components/ui/separator';
import { toast } from '../components/ui/use-toast';
import { AuthContext } from '../context/AuthContext';
import sport_classes from '../enums/sport_classes.json';
import api from '../lib/api';

const BunnyRegistrationPage = () => {
  const { competitionId } = useParams();
  const { control, handleSubmit, reset, } = useForm({
    defaultValues: {
      bunnyId: '',
      classes: [], // Initialize as an empty array
      inCup: false,
    },
  });

  const navigate = useNavigate();


  const [isLoading, setIsLoading] = useState(false);
  const [competition, setCompetition] = useState([]);
  const [bunnies, setBunnies] = useState([]);
  const [classes, setClasses] = useState([]);
  const [selectedBunny, setSelectedBunny] = useState(null);
  const [basket, setBasket] = useState([]); // Basket to hold bunnies and selected classes
  const [successMessage, setSuccessMessage] = useState('');
  const [clubId, setClubId] = useState();
  const [totalPrice, setTotalPrice] = useState(0);
  const [open, setOpen] = useState(false);
  const { auth } = useContext(AuthContext);
  const [smCheckbox, setSmCheckbox] = useState(false);
  const [showSmCheckbox, setShowSmCheckbox] = useState(false);
  const [registeredBunnies, setRegisteredBunnies] = useState([]);
  const [cupGroups, setCupGroups] = useState({});
  const [registeredBunniesLoading, setRegisteredBunniesLoading] = useState(false);


  // Add the selected bunny and its class choices to the basket
  const addToBasket = (data) => {
    const bunny = bunnies.find((b) => b.id === parseInt(data.bunnyId));

    if (!bunny || !bunny.registration_number) {
      toast({
        variant: 'destructive',
        title: 'Ogiltigt registreringsnummer.',
        description: 'Kaninen har inget giltigt registreringsnummer och kan inte anmälas.',
      });
      return;
    }

    if (!data.classes || data.classes.length === 0) {
      toast({
        variant: 'destructive',
        title: 'Ingen klass vald.',
        description: 'Vänligen välj minst en klass innan du fortsätter.',
      });
      return;
    }

    // Check if any of the selected classes have assessment "SM"
    const hasSmClass = data.classes.some((selectedClass) => {
      const realClass = classes.find((c) => c.id === selectedClass.classId);
      return realClass && realClass.assessment === "SM";
    });

    setShowSmCheckbox(hasSmClass); // Show checkbox only if an SM class is selected

    // Add the chosen bunny & classes to the basket
    setBasket((prevBasket) => {
      const existingBunnyIndex = prevBasket.findIndex(
        (item) => item.bunny.id === bunny.id
      );

      // Get previously registered classes for the bunny
      const previouslyRegisteredClasses = registeredBunnies[bunny.id]?.classes || [];

      // Set inCup to true for previously registered classes
      const updatedPreviouslyRegisteredClasses = previouslyRegisteredClasses.map((cls) => ({
        ...cls,
        inCup: true,
      }));

      // Build new array of classes that merges user’s outOfCompetition
      // with forced auto out-of-competition.
      const newClasses = data.classes.map((selectedClass) => {
        let outOfCompetition = !!selectedClass.outOfCompetition;
        const realClass = classes.find((c) => c.id === selectedClass.classId);
        if (!realClass) {
          return {
            classId: selectedClass.classId,
            outOfCompetition,
            inCup: selectedClass.inCup,
            assessment: realClass?.assessment || 'Okänd'
          };
        }

        if (auth?.user?.minimember) {
          if (
            getSportClassNameByRunId(selectedClass.classId)
              .toLowerCase()
              .includes('mini')
          ) {
            outOfCompetition = false;
          }
        } else {
          const scId = realClass.sport_class_id;
          if (scId < bunny.class_straight && scId >= 2 && (bunny.class_straight >= 2 && bunny.class_straight <= 6)) {
            outOfCompetition = true;
          } else if (scId < bunny.class_crooked && scId >= 8 && (bunny.class_crooked >= 8 && bunny.class_crooked <= 11)) {
            outOfCompetition = true;
          } else if (scId < bunny.class_height && scId >= 13 && (bunny.class_height >= 13 && bunny.class_height <= 14)) {
            outOfCompetition = true;
          } else if (scId < bunny.class_length && scId >= 16 && (bunny.class_length >= 16 && bunny.class_length <= 17)) {
            outOfCompetition = true;
          }
        }

        return {
          classId: selectedClass.classId,
          outOfCompetition,
          inCup: selectedClass.inCup,
          assessment: realClass?.assessment || 'Okänd'
        };
      });

      // Group classes by cup
      const cupGroups = classes.reduce((groups, cls) => {
        if (cls.cup && cls.cup.id) {
          if (!groups[cls.cup.id]) {
            groups[cls.cup.id] = { id: cls.cup.id, name: cls.cup.name, classIds: [] };
          }
          groups[cls.cup.id].classIds.push(cls.id);
        }
        return groups;
      }, {});

      // Determine selected cups
      const selectedCups = Object.values(cupGroups).filter((cup) =>
        cup.classIds.some((classId) =>
          newClasses.some((cls) => cls.classId === classId && cls.inCup)
        )
      );

      if (existingBunnyIndex !== -1) {
        const updatedBasket = [...prevBasket];
        const existingBunny = updatedBasket[existingBunnyIndex];

        // Merge existing classes with new classes and previously registered classes
        const mergedClasses = [
          ...existingBunny.classes,
          ...updatedPreviouslyRegisteredClasses,
          ...newClasses
        ].filter((cls, index, self) =>
          self.findIndex((c) => c.classId === cls.classId) === index
        );

        // Merge cups
        const mergedCups = [
          ...existingBunny.cups,
          ...selectedCups,
        ].filter((cup, index, self) => self.findIndex((c) => c.id === cup.id) === index);

        updatedBasket[existingBunnyIndex] = {
          ...existingBunny,
          classes: mergedClasses,
          cups: mergedCups,
        };

        return updatedBasket;
      } else {


        return [
          ...prevBasket,
          {
            bunny,
            classes: [...updatedPreviouslyRegisteredClasses, ...newClasses],
            cups: selectedCups,
            comment: "",
            errors: {},
          },
        ];
      }
    });

    setSelectedBunny(null);
    reset({ bunnyId: '', classes: [], inCup: false });
    setOpen(false);
  };

  // Function to update the comment in the basket
  const updateComment = (bunnyId, newComment) => {
    setBasket((prevBasket) =>
      prevBasket.map((item) =>
        item.bunny.id === bunnyId ? { ...item, comment: newComment } : item
      )
    );
  };

  // Remove a bunny from the basket
  const removeFromBasket = (bunnyId) => {
    setBasket(basket.filter((item) => item.bunny.id !== bunnyId));
  };

  // Submit all bunnies in the basket
  const submitAllBunnies = async () => {
    setIsLoading(true);
    try {
      // Flatten the registrations
      const registrationData = {
        registrations: basket.flatMap((item) =>
          item.classes.map((cls) => ({
            bunnyId: item.bunny.id,
            classId: cls.classId,
            outOfCompetition: cls.outOfCompetition,
            comment: item.comment,
            inCup: cls.inCup,
            registrationId: cls.registrationId
          }))
        ),
      };





      const response = await api.post(
        `https://skhrf-api.cmdigital.se/v1/registrations/participate`,
        registrationData
      );

      if (response.status === 200) {
        setSuccessMessage('All bunnies successfully registered.');
        setBasket([]); // Clear basket after submission
        navigate(`/paymentinformation/${clubId}?atp=${totalPrice}&cpn=${competition.name}&cid=${competitionId}`);
      }
    } catch (error) {

      // Handle validation errors and map them to basket items
      if (error.response && error.response.status === 400) {
        const validationErrors = error?.response?.data?.errors;

        // Create a copy of the basket to update the errors
        const updatedBasket = basket.map((item) => ({ ...item, errors: {} }));

        validationErrors.forEach((err) => {
          // Find the basket item corresponding to err.bunnyId
          const basketItem = updatedBasket.find(
            (item) => item.bunny.id === err.bunnyId
          );
          if (basketItem) {
            // Set the error message for the specific classId
            basketItem.errors[err.classId] = err.message;
          }

          // Optionally show a toast
          toast({
            variant: 'destructive',
            title: 'Något gick fel.',
            description: `Kanin ${err.bunnyId}: ${err.message}`,
          });
        });

        // Update the basket with the errors
        setBasket(updatedBasket);
      } else {
        console.error('Unexpected error during submission:', error);
        toast({
          variant: 'destructive',
          title: 'Något gick fel.',
          description: error?.response?.data?.error,
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getSportClassNameById = (id) => {
    const sportClass = sport_classes.find((sport_class) => sport_class.id === id);
    return sportClass ? sportClass.sport_class_name : "Okänd klass"; // Fallback for invalid IDs
  };

  const getSportClassNameByRunId = useCallback((classId) => {
    const classItem = classes.find((cls) => cls.id === classId);
    return classItem ? getSportClassNameById(classItem.sport_class_id) : "Okänd klass"; // Fallback for invalid IDs
  }, [classes]);

  const getClassNameById = (classId) => {
    const cls = classes.find((c) => c.id === classId);
    return cls ? cls.name : 'Okänd klass'; // Fallback for invalid IDs
  };

  const calculateTotalPrice = useCallback(() => {
    let total = 0;

    basket.forEach((item) => {
      const addedCupIds = new Set();

      item.classes.forEach((cls) => {
        let price = competition.entry_price;

        // Adjust price based on conditions
        if (cls.outOfCompetition) {
          price = competition.ooc_entry_price; // Out of competition price
        } else if (getSportClassNameByRunId(cls.classId).toLowerCase().includes('klassprövning')) {
          price = competition.class_test_price; // Classtest price
        } else if (getSportClassNameByRunId(cls.classId).toLowerCase().includes('mini')) {
          price = competition.mini_entry_price; // Mini class price
        }

        const today = new Date();
        const registrationDate = new Date(competition.registration_date);
        const lateRegistrationDate = new Date(competition.late_registration_date);

        if (!cls.outOfCompetition && today > registrationDate && today <= lateRegistrationDate) {
          price = competition.late_entry_price; // Late registration price
        }

        // Add entry price to total
        total += price;

        // Add cup price only once for each unique cup
        const classCupId = classes.find((c) => c.id === cls.classId)?.cup?.id;
        if (cls.inCup && classCupId && !addedCupIds.has(classCupId)) {
          total += competition.cup_entry_price; // Add cup price
          addedCupIds.add(classCupId); // Mark this cup as added
        }
      });
    });

    setTotalPrice(total);
  }, [basket, classes, competition.class_test_price, competition.cup_entry_price, competition.entry_price, competition.late_entry_price, competition.late_registration_date, competition.mini_entry_price, competition.ooc_entry_price, competition.registration_date, getSportClassNameByRunId]);

  useEffect(() => {
    calculateTotalPrice(); // Recalculate price whenever the basket changes
  }, [calculateTotalPrice]);

  const filterClassesByBunnyAge = useCallback((bunny, classes, competitionDate) => {
    const competitionStartDate = new Date(competitionDate);
    const bunnyBirthDate = new Date(bunny.birth_date);
    const ageInMonths = (competitionStartDate.getFullYear() - bunnyBirthDate.getFullYear()) * 12 +
      competitionStartDate.getMonth() - bunnyBirthDate.getMonth();
    const ageInYears = ageInMonths / 12;

    // Check if the bunny is a veteran in their respective grade
    const isStraightVeteran = (bunny.class_straight === 6);
    const isCrookedVeteran = (bunny.class_crooked === 12);

    return classes.filter((cls) => {
      const sportClassName = getSportClassNameById(cls.sport_class_id).toLowerCase();
      // Check if class is 'Ny-klass', and don't allow registrations for it
      if (cls?.name.toLowerCase().includes('(ny)')) return false;

      // Check eligibility based bunny age
      if (ageInMonths < 5) {
        // Bunny must be at least 5 months old to participate in any class
        return false;
      } else if ((cls.sport_class_id === 4 || cls.sport_class_id === 10) && ageInMonths < 10) {
        // Bunny must be at least 10 months old for "svår" classes (excluding "medelsvår")
        return false;
      } else if ((sportClassName.includes("elit") || sportClassName.includes("höjd") || sportClassName.includes("längd")) && ageInYears < 1) {
        // Bunny must be at least 1 year old for elite, height, or length classes
        return false;
      }

      // If the bunny is a veteran, only allow specific sport_class_id values
      if (isStraightVeteran || isCrookedVeteran) {
        const allowedVeteranClasses = [];
        if (isStraightVeteran) allowedVeteranClasses.push(1, 2, 6);
        if (isCrookedVeteran) allowedVeteranClasses.push(7, 8, 12);

        return allowedVeteranClasses.includes(cls.sport_class_id);
      }

      // Check eligibility based on the bunny's class
      if (cls.sport_class_id <= bunny.class_straight && (bunny.class_straight >= 1 && bunny.class_straight <= 6)) {
        // Bunny's straight class is between 1 and 6, check if class is equal or lower
        return true;
      } else if (cls.sport_class_id <= bunny.class_crooked && cls.sport_class_id >= 7 && (bunny.class_crooked >= 7 && bunny.class_crooked <= 12)) {
        // Bunny's crooked class is between 7 and 12, check if class is equal or lower
        return true;
      } else if (cls.sport_class_id <= bunny.class_height && cls.sport_class_id >= 13 && (bunny.class_height >= 13 && bunny.class_height <= 14)) {
        // Ensure bunnies can participate in height classes appropriately
        return true;
      } else if (cls.sport_class_id <= bunny.class_length && cls.sport_class_id >= 16 && (bunny.class_length >= 16 && bunny.class_length <= 17)) {
        // Ensure bunnies can participate in length classes appropriately
        return true;
      } else if (bunny.class_height === 13 && cls.sport_class_id === 15) {
        return true;
      } else if (bunny.class_length === 16 && cls.sport_class_id === 18) {
        return true;
      }

      return false; // Deny class if it doesn't meet criteria
    });
  }, []);

  const fetchBunniesAndClasses = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await api.get(
        `https://skhrf-api.cmdigital.se/v1/registrations/availability?competitionId=${parseInt(
          competitionId
        )}`
      );



      // Filter out bunnies without registration numbers or with a death date
      const filteredBunnies = response.data.bunnies.filter(
        (bunny) => !bunny.death_date && bunny.registration_number
      );

      const competitionStartDate = response.data.competition.start_date;

      // Filter classes based on user's membership type
      const filteredClasses = filteredBunnies.map((bunny) => {
        const allowedClasses = auth?.user?.minimember
          ? response.data.classes.filter((cls) =>
            getSportClassNameById(cls.sport_class_id).toLowerCase().includes('mini')
          )
          : filterClassesByBunnyAge(bunny, response.data.classes, competitionStartDate);

        return {
          ...bunny,
          allowedClasses,
        };
      });

      // Populate cupGroups
      const newCupGroups = response.data.classes.reduce((groups, cls) => {
        if (cls.cup && cls.cup.id) {
          if (!groups[cls.cup.id]) {
            groups[cls.cup.id] = { id: cls.cup.id, name: cls.cup.name, classIds: [] };
          }
          groups[cls.cup.id].classIds.push(cls.id);
        }
        return groups;
      }, {});

      setCompetition(response.data.competition);
      setBunnies(filteredClasses);
      setClasses(response.data.classes);
      setClubId(response.data.clubId);
      setCupGroups(newCupGroups);
    } catch (error) {
      console.error('Error fetching bunnies and classes:', error);
    } finally {
      setIsLoading(false);
    }
  }, [competitionId, auth?.user?.minimember, filterClassesByBunnyAge]);

  useEffect(() => {
    fetchBunniesAndClasses();
  }, [fetchBunniesAndClasses]);

  const mapGender = (gender) => {
    switch (gender.toLowerCase()) {
      case 'male':
        return 'Hane';
      case 'female':
        return 'Hona';
      default:
        return gender; // fallback if gender is undefined or unexpected
    }
  };

  const mapStatus = (status) => {
    switch (status.toLowerCase()) {
      case 'loaned':
        return <Badge variant='secondary'>Lånad</Badge>;
      case 'owned':
        return <Badge>Egen</Badge>;
      default:
        return status; // fallback if status is undefined or unexpected
    }
  };

  const groupRegistrationsByBunny = useCallback((registrations) => {
    return registrations.reduce((grouped, registration) => {
      const bunnyId = registration.bunnyId;
      const bunnyFullName = registration.bunnyFullName;
      if (!grouped[bunnyId]) {
        grouped[bunnyId] = {
          bunnyFullName,
          bunnyId,
          classes: [],
        };
      }
      // Find the class in the `classes` array to retrieve the assessment
      const cls = classes.find((c) => c.id === registration.classId);
      grouped[bunnyId].classes.push({
        classId: registration.classId,
        outOfCompetition: registration.outOfCompetition,
        inCup: registration.inCup,
        assessment: cls?.assessment || 'Okänd', // Fallback to 'Okänd' if not found
        registrationId: registration.registrationId,
        crossedOut: registration.crossedOut,
      });
      return grouped;
    }, {});
  }, [classes]);

  useEffect(() => {
    const fetchRegisteredBunnies = async () => {
      setRegisteredBunniesLoading(true);
      try {
        const response = await api.get(
          `https://skhrf-api.cmdigital.se/v1/competitions/${competitionId}/participants?onlyMe=true`
        );
        const groupedRegistrations = groupRegistrationsByBunny(response.data[0].registrations);
        setRegisteredBunnies(groupedRegistrations);
      } catch (error) {
        console.error('Error fetching registered bunnies:', error);
      } finally {
        setRegisteredBunniesLoading(false);
      }
    };

    if (classes.length > 0) {
      fetchRegisteredBunnies();
    }
  }, [competitionId, classes, groupRegistrationsByBunny]);

  useEffect(() => {

  }, [basket])

  return (
    <div className="flex w-full items-center justify-center">
      <div className='flex w-5/6 sm:w-3/4 flex-col py-6 gap-3'>
        <h1 className="text-2xl font-semibold mb-4 flex items-center gap-2">
          Anmälan till tävling
          {competition?.registration_date &&
            competition?.late_registration_date &&
            new Date() > new Date(competition.registration_date) &&
            new Date() <= new Date(competition.late_registration_date) && (
              <Badge variant="destructive">Efteranmälning</Badge>
            )}
          {new Date() > new Date(competition?.late_registration_date) &&
            <Badge variant="destructive">Anmälan stängd</Badge>
          }
        </h1>
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 items-start gap-6'>
          {/* Left Side: Bunny and class selection */}
          <div className="md:col-span-3 grid gap-3">
            <div>
              <p><span className='font-medium'>Mini start:</span> {competition.mini_entry_price} kr</p>
              <p><span className='font-medium'>Ordinarie start:</span> {competition.entry_price} kr</p>
              <p><span className='font-medium'>Utom tävlan:</span> {competition.ooc_entry_price} kr</p>
              <p><span className='font-medium'>Cupstart:</span> {competition.cup_entry_price} kr</p>
              <p><span className='font-medium'>Efteranmälan:</span> {competition.late_entry_price} kr</p>
              <p><span className='font-medium'>Klassprövning:</span> {competition.class_test_price} kr</p>
            </div>

            {/* Display isLoading state */}
            {isLoading && <UpdateIcon className='loader-spinner self-center size-6' />}

            {!isLoading && !(new Date() > new Date(competition?.late_registration_date)) && (
              <form onSubmit={handleSubmit(addToBasket)} noValidate className='grid gap-3'>
                {/* Bunny Selection */}
                <div>
                  <Label htmlFor='bunnyId'>Välj kanin</Label>
                  <Controller
                    name="bunnyId"
                    control={control}
                    rules={{ required: 'Vänligen välj en kanin.' }}
                    render={({ field, fieldState }) => {
                      return (
                        <>
                          <Popover modal={true} open={open} onOpenChange={setOpen}>
                            <PopoverTrigger asChild>
                              <Button
                                className={`border p-2 w-full ${fieldState.error ? 'border-red-500' : ''} justify-between flex items-center`}
                                role="combobox"
                                variant="outline"
                                aria-expanded={open}
                                id='bunnyId'
                              >
                                {field.value
                                  ? bunnies.find((b) => b.id === field.value)?.fullName + ' (' + bunnies.find((b) => b.id === field.value)?.registration_number + ')'
                                  : "Välj en kanin"}
                                <CaretSortIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="popover-content-width-full p-0">
                              <Command>
                                <CommandInput placeholder="Sök kanin..." className="h-9" />
                                <CommandList>
                                  <CommandEmpty>Ingen kanin hittades.</CommandEmpty>
                                  <CommandGroup>
                                    {bunnies.map((bunny) => (
                                      <CommandItem
                                        key={bunny.id}
                                        value={bunny.id}
                                        onSelect={() => {
                                          setOpen(false);
                                          setSelectedBunny(bunny);
                                          field.onChange(bunny.id);
                                          reset({ bunnyId: bunny.id, classes: [] }); // Reset classes
                                        }}
                                      >
                                        {bunny.fullName} {bunny.registration_number ? '(' + bunny.registration_number + ')' : null}
                                        <CheckIcon
                                          className={`ml-auto h-4 w-4 ${field.value === bunny.id ? 'opacity-100' : 'opacity-0'
                                            }`}
                                        />
                                      </CommandItem>
                                    ))}
                                  </CommandGroup>
                                </CommandList>
                              </Command>
                            </PopoverContent>
                          </Popover>
                          {fieldState.error && (
                            <p className="text-red-500 mt-1">{fieldState.error.message}</p>
                          )}
                        </>
                      );
                    }}
                  />
                </div>

                {/* Display selected bunny details */}
                {selectedBunny && (
                  <div>
                    <h3 className="font-bold">Vald kanins information:</h3>
                    <div className='grid grid-cols-2 gap-3'>
                      <BunnyInfoItem label='Namn' value={selectedBunny?.name + ' (' + selectedBunny?.registration_number + ')'} isLoading={isLoading} />
                      <BunnyInfoItem label='Färg' value={selectedBunny?.color} isLoading={isLoading} />
                      <BunnyInfoItem label='Kön' value={mapGender(selectedBunny?.gender)} isLoading={isLoading} />
                      <BunnyInfoItem label='Status' value={mapStatus(selectedBunny?.status)} isLoading={isLoading} />
                    </div>
                  </div>
                )}

                {/* Class Selection */}
                {selectedBunny && (
                  <div className="mb-4 flex flex-col items-start">
                    <h3 className="font-bold">Välj klasser</h3>
                    {selectedBunny.allowedClasses.length === 0 ? (
                      <p className="mt-2">
                        Inga startberättigade klasser i tävlingen.
                      </p>
                    ) : (
                      <Controller
                        name="classes"
                        control={control}
                        rules={{
                          validate: (value) => value?.length > 0 || 'Välj minst en klass.', // Validation rule
                        }}
                        render={({ field, fieldState }) => {
                          const selectedClasses = field.value || [];

                          // Build full cup groups from all classes in competition
                          const cupGroups = classes.reduce((groups, cls) => {
                            if (cls.cup && cls.cup.id) {
                              if (!groups[cls.cup.id]) {
                                groups[cls.cup.id] = {
                                  id: cls.cup.id,
                                  name: cls.cup.name,
                                  classIds: [],
                                };
                              }
                              groups[cls.cup.id].classIds.push(cls.id);
                            }
                            return groups;
                          }, {});

                          const anyCupFullySelected = Object.values(cupGroups).some((cup) =>
                            cup.classIds.every((classId) =>
                              selectedClasses.some((selectedClass) => selectedClass.classId === classId)
                            )
                          );

                          const eligibleCups = Object.values(cupGroups).filter((cup) => {
                            // Only display cups where all class IDs match the bunny's eligibility
                            return cup.classIds.every((classId) => {
                              const cls = classes.find((c) => c.id === classId);
                              if (!cls) return false; // Skip invalid classes
                              // Check if bunny qualifies for this class based on sport_class_id
                              return (
                                cls.sport_class_id === selectedBunny.class_straight ||
                                cls.sport_class_id === selectedBunny.class_crooked ||
                                cls.sport_class_id === selectedBunny.class_height ||
                                cls.sport_class_id === selectedBunny.class_length
                              );
                            });
                          });

                          {/* Determine if any cup should be displayed */ }
                          const shouldShowCups = Object.values(cupGroups).some((cup) => {
                            const cupClassIds = cup.classIds;

                            // Get classes that are already registered for this bunny
                            const alreadyRegisteredClasses = registeredBunnies[selectedBunny?.id]?.classes || [];

                            // Get classes that are currently selected in the form
                            const currentlySelectedClassIds = selectedClasses.map(sc => sc.classId);

                            // Combine already registered class IDs with currently selected class IDs
                            const allSelectedClassIds = [
                              ...alreadyRegisteredClasses.map(rc => rc.classId),
                              ...currentlySelectedClassIds
                            ];

                            // Check if all classes required for at least one cup are selected
                            return cupClassIds.every(cupClassId => allSelectedClassIds.includes(cupClassId));
                          });

                          return (
                            <>
                              {selectedBunny.allowedClasses.map((cls) => {
                                const isClassSelected = selectedClasses.some((item) => item.classId === cls.id);
                                const currentClass = selectedClasses.find((item) => item.classId === cls.id);

                                // Check if the bunny is already registered to this class
                                const isAlreadyRegistered = registeredBunnies[selectedBunny.id]?.classes.some(
                                  (registeredClass) => registeredClass.classId === cls.id
                                );

                                return (
                                  <div key={cls.id}>
                                    <div className="flex items-center mt-3 gap-3">
                                      <Checkbox
                                        checked={isClassSelected || (isAlreadyRegistered ? true : false)}
                                        onCheckedChange={(checked) => {
                                          if (checked) {
                                            field.onChange([...selectedClasses, { classId: cls.id, outOfCompetition: false }]);
                                          } else {
                                            field.onChange(selectedClasses.filter((item) => item.classId !== cls.id));
                                          }
                                        }}
                                        id={cls.id}
                                        key={cls.id}
                                        name='classSelect'
                                        disabled={isAlreadyRegistered}
                                      />
                                      <Label htmlFor={cls.id}>
                                        {cls.name + ' ' || 'Utan namn '}
                                        {!(cls.name.toLowerCase().includes('höjd') || cls.name.toLowerCase().includes('längd')) &&
                                          '- Bedömning ' + (cls.assessment || 'Okänd')}
                                        {cls.cup.id && <Badge className="ml-2" variant="secondary">{cls.cup.name}</Badge>}
                                      </Label>
                                    </div>
                                    {isClassSelected && (
                                      <div className="flex items-center ml-4 mt-2 gap-3">
                                        <Checkbox
                                          checked={currentClass?.outOfCompetition || false}
                                          onCheckedChange={(checked) => {
                                            const updatedValue = selectedClasses.map(
                                              (item) =>
                                                item.classId === cls.id
                                                  ? {
                                                    ...item,
                                                    outOfCompetition: checked,
                                                  }
                                                  : item
                                            );
                                            field.onChange(updatedValue);
                                          }}
                                          key={`outOfCompetition-${cls.id}`}
                                          id={`outOfCompetition-${cls.id}`}
                                          name='outOfCompetitionCheckbox'
                                        />
                                        <Label htmlFor={`outOfCompetition-${cls.id}`}>
                                          Utom tävlan
                                        </Label>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                              {fieldState.error && (
                                <p className="text-red-500 mt-1">{fieldState.error.message}</p>
                              )}

                              {/* Conditionally render "Cuper" section */}
                              {shouldShowCups && (
                                <div className="mt-4">
                                  <h4 className="font-bold">Cuper</h4>
                                  {Object.values(cupGroups).map((cup) => {
                                    // Get all classes needed for this cup
                                    const cupClassIds = cup.classIds;

                                    // Get already registered classes for this bunny
                                    const alreadyRegisteredClasses = registeredBunnies[selectedBunny?.id]?.classes || [];

                                    // Get classes currently selected in the form
                                    const currentlySelectedClassIds = selectedClasses.map(sc => sc.classId);

                                    // Combine already registered and currently selected class IDs
                                    const allSelectedClassIds = [
                                      ...alreadyRegisteredClasses.map(rc => rc.classId),
                                      ...currentlySelectedClassIds
                                    ];

                                    // Check if all classes required for the cup are selected
                                    const allCupClassesSelected = cupClassIds.every(cupClassId =>
                                      allSelectedClassIds.includes(cupClassId)
                                    );

                                    // Determine if the bunny is already registered in this cup
                                    const isAlreadyInCup = cupClassIds.every(cupClassId =>
                                      alreadyRegisteredClasses.some(rc => rc.classId === cupClassId && rc.inCup)
                                    );

                                    // If not all required classes are selected, do not show this cup
                                    if (!allCupClassesSelected) {
                                      return null;
                                    }

                                    // Get only the cup classes that are in the current form (not already registered)
                                    const currentFormCupClasses = selectedClasses.filter(sc => cupClassIds.includes(sc.classId));

                                    // Check if all eligible classes in the current form are marked for the cup
                                    const allCurrentFormClassesInCup = currentFormCupClasses.every(sc => sc.inCup);

                                    return (
                                      <div key={cup.id} className="flex items-center mt-2 gap-3">
                                        <Checkbox
                                          id={`cup-checkbox-${cup.id}`}
                                          key={`cup-checkbox-${cup.id}`}
                                          checked={allCurrentFormClassesInCup}
                                          onCheckedChange={(checked) => {
                                            const updatedClasses = selectedClasses.map((sc) => {
                                              if (cup.classIds.includes(sc.classId)) {
                                                return { ...sc, inCup: checked };
                                              }
                                              return sc;
                                            });
                                            field.onChange(updatedClasses);
                                          }}
                                          name='cupCheckbox'
                                          disabled={isAlreadyInCup}
                                        />
                                        <Label htmlFor={`cup-checkbox-${cup.id}`}>
                                          Anmäl till {cup.name}
                                        </Label>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </>
                          );
                        }}
                      />

                    )}
                  </div>
                )}

                {/* Add to Basket Button */}
                {(selectedBunny && selectedBunny.allowedClasses.length > 0) && (
                  <Button type="submit" disabled={isLoading}>
                    {isLoading
                      ? 'Lägger till...'
                      : `Lägg till ${selectedBunny.name}`}
                  </Button>
                )}
              </form>
            )}
          </div>

          {/* Right Sidebar: Basket */}
          <div className="p-4 bg-gray-100 h-fit md:col-span-2 rounded-md">
            <h2 className="text-xl font-semibold mb-2">Kaniner att anmäla</h2>

            {/* Display the basket */}
            {basket.length === 0 ? (
              <p>Du har inte valt några kaniner ännu.</p>
            ) : (
              <div>
                {basket.map((item) => (
                  <div
                    name='basketItem'
                    key={item.bunny.id}
                    className={`mb-4 p-4 border ${Object.keys(item.errors).length > 0
                      ? 'border-red-500'
                      : 'border-gray-300'
                      } bg-white rounded-md`}
                  >
                    <div className='flex justify-between items-center'>
                      <h3 className="font-bold flex items-center mb-3">{item.bunny.fullName} {item.comment && <ChatBubbleIcon className='ml-3 overflow-visible' />}</h3>
                      <Dialog modal={true}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant='ghost'><DotsHorizontalIcon className='size-5' /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent className='dropdown-content-width-full'>
                            <DropdownMenuLabel>Verktyg</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <AddRegistrationCommentDialog
                              currentComment={item.comment} // Pass the current comment
                              onSave={(newComment) => {
                                updateComment(item.bunny.id, newComment); // Updates the state
                              }}
                              useApi={false}
                              dropdown={true}
                            />
                            <DropdownMenuItem onClick={() => removeFromBasket(item.bunny.id)} className='cursor-pointer'>
                              Ta bort
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </Dialog>
                    </div>
                    {/* <p>Klasser:</p> */}
                    <ul>
                      {item.classes.map((cls) => (
                        <Fragment key={cls.classId}>
                          <li
                            name='cupInBasket'
                            className='text-sm grid grid-cols-1 md:flex justify-between'>
                            {getClassNameById(cls.classId)} - Bedömning {cls.assessment}
                            {cls.outOfCompetition ? <Badge className='md:ml-3 w-fit min-w-24 h-fit justify-center' variant='secondary'>Utom tävlan</Badge> : ''}

                            {item.errors && item.errors[cls.classId] && (
                              <p className="text-red-500 pl-1">
                                {item.errors[cls.classId]}
                              </p>
                            )}
                          </li>
                          {item.classes.length > 1 &&
                            <Separator className='my-2 last:hidden' />
                          }
                        </Fragment>
                      ))}
                    </ul>
                    {/* Render Selected Cups */}
                    {item.cups?.length > 0 && (
                      <div className="mt-3">
                        <h4 className="font-bold">Valda cuper</h4>
                        <ul>
                          {item.cups.map((cup) => (
                            <li key={cup.id} className="text-sm">
                              {cup.name}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                  </div>
                ))}
                <div>
                  Att betala: {totalPrice} kr
                </div>
                {showSmCheckbox &&
                  <div className='pt-2 flex items-center gap-2'>
                    <Label className="flex gap-2 items-center">
                      <Checkbox checked={smCheckbox} onCheckedChange={(val) => setSmCheckbox(val)} />
                      Härmed intygar jag att kaninerna jag anmäler får tävla i SM.
                    </Label>
                  </div>
                }
                <Button
                  className="mt-4 w-full"
                  type="button"
                  onClick={submitAllBunnies}
                  disabled={isLoading || (showSmCheckbox && !smCheckbox)}
                >
                  {isLoading ? <UpdateIcon className='loader-spinner' /> : <>Anmäl kaniner</>}
                </Button>

                {/* Success Message */}
                {successMessage && (
                  <p className="text-green-500 mt-4">{successMessage}</p>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="p-4 bg-gray-100 h-fit md:col-span-2 rounded-md">
          <h2 className="text-xl font-semibold mb-2">Redan anmälda kaniner</h2>
          {Object.keys(registeredBunnies).length === 0 || registeredBunniesLoading ? (
            <p>Inga kaniner är anmälda ännu.</p>
          ) : (
            <div>
              {Object.values(registeredBunnies).map((registration) => {
                const bunny = bunnies.find((b) => b.id === registration.bunnyId);

                // Check if bunny is crossedOut in any class
                const isBunnyCrossedOut = registration.classes.some((cls) => cls.crossedOut);

                // Check if bunny is outOfCompetition in any class
                const isBunnyOutOfCompetition = registration.classes.some((cls) => cls.outOfCompetition);

                // Find the cups the bunny is already registered in
                const registeredCups = Object.values(cupGroups).filter((cup) =>
                  cup.classIds.every((classId) =>
                    registration.classes.some(
                      (cls) => cls.classId === classId && cls.inCup
                    )
                  )
                );

                return (
                  <div key={registration.bunnyId} className="mb-4 p-4 border border-gray-300 bg-white rounded-md">
                    <h3 className="font-bold">{registration?.bunnyFullName}</h3>
                    <ul>
                      {registration.classes.map((cls, index) => (
                        <Fragment key={cls.classId}>
                          <li className="text-sm grid grid-cols-1 md:flex justify-between">
                            {getClassNameById(cls.classId)} - Bedömning {cls.assessment || 'Okänd'}
                            <div className='flex md:flex-col gap-1'>
                              {cls.outOfCompetition && (
                                <Badge className="md:ml-3 w-fit min-w-24 h-fit justify-center" variant="secondary">
                                  Utom tävlan
                                </Badge>
                              )}
                              {cls.crossedOut && (
                                <Badge className="md:ml-3 w-fit min-w-24 h-fit justify-center" variant="destructive">
                                  Struken
                                </Badge>
                              )}
                            </div>
                          </li>
                          {registration.classes.length > 1 && index !== registration.classes.length - 1 && (
                            <Separator className="my-2" />
                          )}
                        </Fragment>
                      ))}
                    </ul>

                    {/* Show Already Registered Cups */}
                    {registeredCups.length > 0 && (
                      <div>
                        <Separator className='my-2' />
                        <ul className="flex flex-wrap gap-2 mt-1">
                          {registeredCups.map((cup) => (
                            <Badge key={cup.id}>
                              {cup.name}
                            </Badge>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Cup Participation Buttons */}
                    <div className='flex gap-3 mt-1'>
                      {Object.values(cupGroups).map((cup) => {
                        const cupClassIds = cup.classIds;
                        const selectedClasses = registration.classes.filter((sc) =>
                          cupClassIds.includes(sc.classId)
                        );

                        const allCupClassesSelected = cupClassIds.every((classId) =>
                          selectedClasses.some((selectedClass) => selectedClass.classId === classId)
                        );

                        const allInCup = selectedClasses.every((selectedClass) => selectedClass.inCup);

                        if (allCupClassesSelected && !allInCup && !isBunnyCrossedOut && !isBunnyOutOfCompetition) {
                          return (
                            <div key={cup.id} className="flex items-center mt-2 gap-3">
                              <Button
                                id={`cup-button-${cup.id}`}

                                variant='secondary'
                                onClick={() => {
                                  const updatedClasses = selectedClasses.map((sc) => ({
                                    ...sc,
                                    inCup: true, // Ensure only the relevant classes are marked as inCup
                                    registrationId: sc.registrationId,
                                  }));

                                  setBasket((prevBasket) => {
                                    const existingBunnyIndex = prevBasket.findIndex(
                                      (item) => item.bunny.id === bunny.id
                                    );

                                    if (existingBunnyIndex !== -1) {
                                      const updatedBasket = [...prevBasket];
                                      const existingBunny = updatedBasket[existingBunnyIndex];

                                      // Filter only relevant classes and prevent duplicates
                                      const mergedClasses = [
                                        ...existingBunny.classes.filter(
                                          (existingClass) => !cupClassIds.includes(existingClass.classId)
                                        ),
                                        ...updatedClasses,
                                      ];

                                      // Prevent duplicate cup entries
                                      const mergedCups = [
                                        ...existingBunny.cups,
                                        { id: cup.id, name: cup.name },
                                      ].filter((cup, index, self) => self.findIndex((c) => c.id === cup.id) === index);

                                      updatedBasket[existingBunnyIndex] = {
                                        ...existingBunny,
                                        classes: mergedClasses,
                                        cups: mergedCups,
                                      };

                                      return updatedBasket;
                                    } else {
                                      return [
                                        ...prevBasket,
                                        {
                                          bunny,
                                          classes: updatedClasses, // Only the specific cup's classes
                                          cups: [{ id: cup.id, name: cup.name }],
                                          comment: "",
                                          errors: {},
                                        },
                                      ];
                                    }
                                  });
                                }}
                              >
                                Anmäl till {cup.name}
                              </Button>
                            </div>
                          );
                        }
                        return null;
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

    </div>
  );
};

export default BunnyRegistrationPage;
